import { zodResolver } from "@hookform/resolvers/zod";
import { useState } from "react";
import { useForm } from "react-hook-form";

import { Button } from "@/components/button";
import {
  Dialog,
  DialogBody,
  DialogCloseButton,
  DialogFooter,
  DialogHeader,
} from "@/components/dialog";
import { Modal } from "@/components/modal";

import { useCreateBrandNote } from "../../hooks/useCreateBrandNote";
import {
  type BrandKnowledgeNoteFormValues,
  BrandKnowledgeNoteSchema,
} from "../../schema";
import { NewNoteFormFields } from "../NewNoteFormFields";
import { DiscardNoteModal } from "./DiscardNoteModal";

interface NewNoteModalProps {
  readonly brandId: number;
  readonly isOpen: boolean;
  readonly onClose: () => void;
}

export function NewNoteModal({ brandId, isOpen, onClose }: NewNoteModalProps) {
  const {
    control,
    formState: { errors, isDirty, isSubmitting },
    handleSubmit,
    reset,
  } = useForm<BrandKnowledgeNoteFormValues>({
    defaultValues: {
      description: "",
      name: "",
      noteType: "" as unknown as BrandKnowledgeNoteFormValues["noteType"],
    },
    resolver: zodResolver(BrandKnowledgeNoteSchema),
  });

  const [isDiscardModalOpen, setIsDiscardModalOpen] = useState(false);
  const [editorKey, setEditorKey] = useState(0);

  const { mutateAsync: createNote } = useCreateBrandNote({ brandId });

  const onSubmit = async (data: BrandKnowledgeNoteFormValues) => {
    await createNote(data);
    reset();
    onClose();
  };

  const handleClose = () => {
    if (isDirty) {
      setIsDiscardModalOpen(true);
    } else {
      onClose();
    }
  };

  const handleClear = () => {
    reset();
    setEditorKey((key) => key + 1);
  };

  return (
    <>
      <Modal isOpen={isOpen} onOpenChange={handleClose}>
        <Dialog>
          <DialogHeader>New note</DialogHeader>
          <DialogCloseButton />
          <DialogBody>
            <form
              className="flex flex-col gap-6"
              id="new-note-form"
              onSubmit={handleSubmit(onSubmit)}
            >
              <p className="text-text-tertiary">
                Add key information about the account or brand, such as
                subscription updates, requirements, or design guidelines.
              </p>
              <NewNoteFormFields
                control={control}
                editorKey={editorKey}
                errors={errors}
              />
            </form>
          </DialogBody>
          <DialogFooter>
            <div className="flex items-center gap-3">
              <Button onPress={handleClear} variant="secondary">
                Clear
              </Button>
              <Button
                form="new-note-form"
                isDisabled={isSubmitting}
                type="submit"
              >
                Save
              </Button>
            </div>
          </DialogFooter>
        </Dialog>
      </Modal>

      <DiscardNoteModal
        isOpen={isDiscardModalOpen}
        onClose={() => setIsDiscardModalOpen(false)}
        onConfirm={() => {
          reset();
          setIsDiscardModalOpen(false);
          onClose();
        }}
      />
    </>
  );
}
